import {
    CircularProgress,
    Grid,
    TextField,
    Typography,
} from "@material-ui/core"
import Fab from "@material-ui/core/Fab"
import Link from "@material-ui/core/Link"
import { observable } from "mobx"
import { observer } from "mobx-react"
import * as React from "react"
import { Link as RouterLink } from "react-router-dom"
import { CCUtils, NumberMaybe } from "../../common/CCUtils"
import {
    BIRTHDAY_DAY,
    BIRTHDAY_MONTH,
    LOGIN_NAME,
} from "../../common/LocalConfig"
import { MonthMaybe, TimeUtils } from "../../common/TimeUtils"
import { Student } from "../../shared/models/Student"
import { appTheme } from "../StyleUtils"
import { LoginRoutes } from "../WebApp"
import { AuthStoreSA } from "./AuthStoreSA"
import { BirthdayPicker } from "./BirthdayPicker"
import { LoginDialog } from "./LoginDialog"
import { LoginPage } from "./LoginPage"
import { fbAuth, fbFunctions } from "../../common/FirebaseConfig"
import { log } from "../../common/Logging"

class StudentLoginStore {
    @observable
    studentId = CCUtils.debug && LOGIN_NAME ? LOGIN_NAME : ""
    @observable
    birthMonth: MonthMaybe = CCUtils.debug && LOGIN_NAME ? BIRTHDAY_MONTH : ""
    @observable
    birthDay: NumberMaybe = CCUtils.debug && LOGIN_NAME ? BIRTHDAY_DAY : ""
    @observable
    loginMessage = "Login"
    @observable
    errorMessage: string
    @observable
    dialogIsOpen = false
    @observable
    restrictions?: {
        title: string
        message: string
    }
    @observable
    student: Student

    inputIsValid = () =>
        this.studentId.length > 0 &&
        this.birthMonth !== "" &&
        this.birthDay !== ""

    handleUpdate = (event: React.ChangeEvent<HTMLInputElement>) => {
        this[event.target.name] = event.target.value
    }
}

@observer
export class StudentLoginPage extends React.Component {
    store = new StudentLoginStore()
    authStore = AuthStoreSA.instance
    audioElement = React.createRef<HTMLAudioElement>()

    @observable
    loading = false

    attemptConfirmation = async (e) => {
        e.preventDefault()
        this.loading = true
        this.store.loginMessage = "Checking credentials"
        this.stopAudio()
        const { birthMonth, birthDay, studentId } = this.store
        if (birthMonth === "" || birthDay === "") {
            this.store.errorMessage = "Please enter a month and day."
        } else {
            const studentIdLowerCase = studentId.toLowerCase()
            const birthCode = TimeUtils.birthMonthAndDayToBirthCode(
                birthMonth,
                birthDay,
            )

            let success
            try {
                const authTokenResult = await fbFunctions().httpsCallable(
                    "getStudentAuthToken",
                )({ studentId, birthCode })
                const authToken = authTokenResult.data
                this.store.loginMessage = "Getting student data"

                await fbAuth().signInWithCustomToken(authToken)
                log.debug(`logged in student ${studentId}-${birthCode}`)
                success = true
            } catch (e) {
                log.error(`Couldn't login student! ${studentId}-${birthCode}`)
                success = false
            }

            if (success) {
                this.store.loginMessage = "Signing in"
                const student = await this.authStore.findStudent(
                    studentIdLowerCase,
                    birthCode,
                )
                if (student == null) {
                    this.store.errorMessage = "Couldn't find student"
                } else {
                    this.store.student = student
                    this.store.dialogIsOpen = true
                }
            } else {
                this.store.errorMessage = "Incorrect student id or birthday."
            }
        }

        this.store.loginMessage = "Login"
        this.loading = false
    }

    cancelDialog = () => {
        this.store.student = undefined
        this.store.dialogIsOpen = false
    }

    stopAudio = () => {
        const audioElem = this.audioElement.current as HTMLAudioElement
        audioElem.pause()
    }

    render() {
        const {
            studentId,
            student,
            inputIsValid,
            handleUpdate,
            loginMessage,
            errorMessage,
            birthMonth,
            birthDay,
            dialogIsOpen,
        } = this.store

        let audioFile
        const enterIdAudioFile = CCUtils.asset(
            "auth/login/enter-student-id-birthday.mp3",
        )
        const dontMatchAudioFile = CCUtils.asset(
            "auth/login/student-id-birthday-do-not-match.mp3",
        )

        if (errorMessage) {
            audioFile = dontMatchAudioFile
        } else {
            audioFile = enterIdAudioFile
        }

        return (
            <LoginPage title={"Student Login"}>
                <audio ref={this.audioElement} src={audioFile} autoPlay />
                {dialogIsOpen && student && (
                    <LoginDialog
                        dialogIsOpen={dialogIsOpen}
                        onClose={this.cancelDialog}
                        student={student}
                    />
                )}
                {errorMessage && (
                    <Typography
                        color={"error"}
                        style={{ marginBottom: appTheme.spacing(2) }}
                    >
                        Your student ID and birthday do not match, check that
                        they are correct. If this is your first time logging in
                        to Click City, check with your teacher.
                    </Typography>
                )}
                <form
                    onSubmit={this.attemptConfirmation}
                    style={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        padding: appTheme.spacing(0, 4),
                    }}
                >
                    <Grid container={true}>
                        <Grid item={true} xs={12}>
                            <TextField
                                onChange={handleUpdate}
                                label={"Student Id"}
                                name={"studentId"}
                                value={studentId}
                                fullWidth={true}
                                autoFocus={true}
                                inputProps={{
                                    autoCapitalize: "none",
                                }}
                            />
                        </Grid>
                        <Grid item={true} xs={12}>
                            <BirthdayPicker
                                birthDay={birthDay}
                                birthMonth={birthMonth}
                                handleUpdate={handleUpdate}
                            />
                        </Grid>
                    </Grid>

                    <Fab
                        color={"primary"}
                        disabled={!inputIsValid() || this.loading}
                        type={"submit"}
                        style={{
                            margin: appTheme.spacing(4, 0),
                            width: "100%",
                        }}
                    >
                        {loginMessage}
                        {this.loading && (
                            <CircularProgress
                                color={"inherit"}
                                size={20}
                                style={{ marginLeft: appTheme.spacing(1) }}
                            />
                        )}
                    </Fab>
                </form>
                <div
                    style={{
                        display: "flex",
                        marginBottom: appTheme.spacing(2),
                    }}
                >
                    <Typography variant={"body2"}>
                        Don't have a Login?{" "}
                        <Link component={RouterLink} to={LoginRoutes.joinClass}>
                            Join a Class
                        </Link>
                    </Typography>
                </div>
            </LoginPage>
        )
    }
}
